// app/javascript/controllers/mapbox_controller.js
import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder'
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css'

export default class extends Controller {
  static targets = ["map", "latitude", "longitude", "form", "error", "admin0", "admin1", "admin2"]
  static values = {
    apiKey: String,
    geojsonUrl: String
  }
  
  connect() {
    if (this.hasApiKeyValue) {
      mapboxgl.accessToken = this.apiKeyValue
      this.initializeMap()
    } else {
      console.error("Mapbox API key is missing")
      this.errorTarget.textContent = "Erreur de configuration de la carte"
      this.errorTarget.classList.remove('hidden')
    }
    this.updateCoordinates(42.590275, 11.825138)
  }

  async initializeMap() {
    this.map = new mapboxgl.Map({
      container: this.mapTarget,
      style: 'mapbox://styles/mapbox/streets-v12', // Changé de satellite-streets à streets
      center: [42.590275, 11.825138], // Centre de Djibouti
      zoom: 7.5
    })

    // Ajouter le geocoder
    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      countries: 'dj', // Limiter à Djibouti
      language: 'fr'
    })

    this.map.addControl(geocoder)
    this.map.addControl(new mapboxgl.NavigationControl(), 'top-right')

    // Ajouter un marqueur qui peut être déplacé
    this.marker = new mapboxgl.Marker({
      draggable: true
    })
      .setLngLat([42.590275, 11.825138])
      .addTo(this.map)

    // Définir les coordonnées initiales
    this.updateCoordinates(42.590275, 11.825138)

    // Gérer le déplacement du marqueur
    this.marker.on('dragend', () => {
      const lngLat = this.marker.getLngLat()
      this.updateCoordinates(lngLat.lng, lngLat.lat)
      this.showAdminInfo(lngLat.lng, lngLat.lat)
    })

    // Gérer le clic sur la carte
    this.map.on('click', (e) => {
      const { lng, lat } = e.lngLat
      this.marker.setLngLat([lng, lat])
      this.updateCoordinates(lng, lat)
      this.showAdminInfo(lng, lat)
    })

    // Gérer la sélection via le geocoder
    geocoder.on('result', (e) => {
      const [lng, lat] = e.result.center
      this.marker.setLngLat([lng, lat])
      this.updateCoordinates(lng, lat)
      this.showAdminInfo(lng, lat)
    })

    // Charger et afficher les frontières administratives
    this.map.on('load', () => {
      this.loadAdministrativeBoundaries()
    })
  }

  updateCoordinates(longitude, latitude) {
    if (this.hasLatitudeTarget && this.hasLongitudeTarget) {
      this.latitudeTarget.value = latitude
      this.longitudeTarget.value = longitude
    }
  }

  // Gestion des sélections en cascade
  async admin0Changed(event) {
    const admin0Id = event.target.value
    const admin1Select = document.getElementById('operational_area_admin1_id')
    const admin2Select = document.getElementById('operational_area_admin2_id')
    
    // Réinitialiser les selects dépendants
    admin1Select.innerHTML = '<option value="">Sélectionner une région</option>'
    admin2Select.innerHTML = '<option value="">Sélectionner une sous-préfecture</option>'
    admin2Select.disabled = true
    
    if (admin0Id) {
      admin1Select.disabled = false
      
      try {
        const response = await fetch(`/api/geojson/admin1_options/${admin0Id}`)
        const admin1s = await response.json()
        
        admin1s.forEach(admin1 => {
          const option = document.createElement('option')
          option.value = admin1.id
          option.textContent = admin1.name
          admin1Select.appendChild(option)
        })
      } catch (error) {
        console.error('Erreur lors du chargement des régions:', error)
      }
    } else {
      admin1Select.disabled = true
    }
  }

  async admin1Changed(event) {
    const admin1Id = event.target.value
    const admin2Select = document.getElementById('operational_area_admin2_id')
    
    // Réinitialiser le select des sous-préfectures
    admin2Select.innerHTML = '<option value="">Sélectionner une sous-préfecture</option>'
    
    if (admin1Id) {
      admin2Select.disabled = false
      
      try {
        const response = await fetch(`/api/geojson/admin2_options/${admin1Id}`)
        const admin2s = await response.json()
        
        admin2s.forEach(admin2 => {
          const option = document.createElement('option')
          option.value = admin2.id
          option.textContent = admin2.name
          admin2Select.appendChild(option)
        })
      } catch (error) {
        console.error('Erreur lors du chargement des sous-préfectures:', error)
      }
    } else {
      admin2Select.disabled = true
    }
  }

  async showAdminInfo(longitude, latitude) {
    try {
      // Requête au service pour obtenir les informations administratives
      const response = await fetch('/api/geojson/admin_lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          latitude: latitude,
          longitude: longitude
        })
      })

      if (response.ok) {
        const data = await response.json()
        this.displayAdminInfo(data)
        this.storeDetectedAdmin(data)
      }
    } catch (error) {
      console.log('Impossible de récupérer les informations administratives:', error)
    }
  }

  displayAdminInfo(adminData) {
    const detectedInfoDiv = document.getElementById('detected-admin-info')
    const contentDiv = document.getElementById('detected-admin-content')
    
    if (adminData && adminData.admin1) {
      contentDiv.innerHTML = `
        <p class="text-sm"><strong>Pays:</strong> ${adminData.admin0.name}</p>
        <p class="text-sm"><strong>Région:</strong> ${adminData.admin1.name}</p>
        ${adminData.admin2 ? `<p class="text-sm"><strong>Sous-préfecture:</strong> ${adminData.admin2.name}</p>` : ''}
      `
      detectedInfoDiv.classList.remove('hidden')
    } else {
      detectedInfoDiv.classList.add('hidden')
    }
  }

  storeDetectedAdmin(adminData) {
    // Stocker les données détectées pour utilisation ultérieure
    this.detectedAdminData = adminData
  }

  useDetectedAdmin() {
    if (!this.detectedAdminData) return
    
    const admin0Select = document.getElementById('operational_area_admin0_id')
    const admin1Select = document.getElementById('operational_area_admin1_id')
    const admin2Select = document.getElementById('operational_area_admin2_id')
    
    // Mettre à jour les champs cachés
    this.admin0Target.value = this.detectedAdminData.admin0.id
    this.admin1Target.value = this.detectedAdminData.admin1.id
    if (this.detectedAdminData.admin2) {
      this.admin2Target.value = this.detectedAdminData.admin2.id
    }
    
    // Sélectionner le pays
    admin0Select.value = this.detectedAdminData.admin0.id
    
    // Déclencher le changement pour charger les régions
    this.admin0Changed({ target: admin0Select }).then(() => {
      // Sélectionner la région
      admin1Select.value = this.detectedAdminData.admin1.id
      
      // Déclencher le changement pour charger les sous-préfectures
      this.admin1Changed({ target: admin1Select }).then(() => {
        // Sélectionner la sous-préfecture si elle existe
        if (this.detectedAdminData.admin2) {
          admin2Select.value = this.detectedAdminData.admin2.id
        }
      })
    })
  }

  updateSectors(event) {
    console.log('Secteur d\'intervention modifié:', event.target.value, event.target.checked)
  }

  async createOperationalArea(event) {
    event.preventDefault()
    
    // Déterminer si c'est une création ou une mise à jour en vérifiant l'URL
    const isUpdate = this.formTarget.action.includes('/operational_areas/')
    
    // Vérifier les coordonnées et données administratives uniquement pour la création
    if (!isUpdate) {
      // Vérifier si les coordonnées sont valides
      if (!this.latitudeTarget.value || !this.longitudeTarget.value) {
        this.errorTarget.textContent = "Veuillez sélectionner un emplacement sur la carte"
        this.errorTarget.classList.remove('hidden')
        return
      }
      
      // Vérifier si les données administratives ont été détectées
      if (!this.detectedAdminData) {
        this.errorTarget.textContent = "Veuillez attendre la détection de la zone administrative"
        this.errorTarget.classList.remove('hidden')
        return
      }
    }
    
    // Récupérer les secteurs d'intervention sélectionnés
    const interventionSectorIds = Array.from(
      document.querySelectorAll('input[name="intervention_sector_ids[]"]:checked')
    ).map(checkbox => checkbox.value)
    
    console.log('Secteurs d\'intervention sélectionnés:', interventionSectorIds)
    
    try {
      const method = isUpdate ? 'PATCH' : 'POST'
      
      // Récupérer l'ID de l'utilisateur depuis le meta tag
      const userId = document.querySelector('meta[name="user-id"]')?.content
      
      if (!userId) {
        this.errorTarget.textContent = "Erreur : utilisateur non identifié"
        this.errorTarget.classList.remove('hidden')
        return
      }
      
      const formData = {
        operational_area: {
          latitude: this.latitudeTarget.value,
          longitude: this.longitudeTarget.value,
          admin0_id: this.detectedAdminData?.admin0?.id,
          admin1_id: this.detectedAdminData?.admin1?.id,
          admin2_id: this.detectedAdminData?.admin2?.id,
          intervention_sector_ids: interventionSectorIds,
          created_by_id: userId
        }
      }
      
      console.log('Données envoyées:', formData)
      
      const response = await fetch(this.formTarget.action, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify(formData)
      })

      if (response.ok) {
        // Extraire l'ID de l'organisation de l'URL du formulaire
        const organizationId = this.formTarget.action.split('/organizations/')[1].split('/')[0]
        window.location.href = `/members/organizations/${organizationId}`
      } else {
        const data = await response.json()
        this.errorTarget.textContent = data.error || "Une erreur est survenue"
        this.errorTarget.classList.remove('hidden')
      }
    } catch (error) {
      console.error('Erreur lors de la création/mise à jour de la zone opérationnelle:', error)
      this.errorTarget.textContent = "Une erreur est survenue lors de la création/mise à jour de la zone opérationnelle"
      this.errorTarget.classList.remove('hidden')
    }
  }

  async loadAdministrativeBoundaries() {
    if (!this.hasGeojsonUrlValue) {
      console.log('No GeoJSON URL provided')
      return
    }

    try {
      console.log('Loading GeoJSON from:', this.geojsonUrlValue)
      const response = await fetch(this.geojsonUrlValue)
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const geojsonData = await response.json()
      console.log('GeoJSON loaded successfully:', geojsonData.features?.length, 'features')

      // Ajouter la source GeoJSON
      this.map.addSource('admin-boundaries', {
        type: 'geojson',
        data: geojsonData
      })

      // Couche de remplissage pour les sous-préfectures (très transparent)
      this.map.addLayer({
        id: 'admin2-fill',
        type: 'fill',
        source: 'admin-boundaries',
        filter: ['has', 'ADM2_EN'],
        paint: {
          'fill-color': '#e1f5fe',
          'fill-opacity': 0.1
        }
      })

      // Couche de remplissage pour les régions (plus visible)
      this.map.addLayer({
        id: 'admin1-fill',
        type: 'fill',
        source: 'admin-boundaries',
        filter: ['has', 'ADM1_EN'],
        paint: {
          'fill-color': [
            'case',
            ['==', ['get', 'ADM1_EN'], 'Ali Sabieh'], '#e3f2fd',
            ['==', ['get', 'ADM1_EN'], 'Arta'], '#bbdefb',
            ['==', ['get', 'ADM1_EN'], 'Dikhil'], '#90caf9',
            ['==', ['get', 'ADM1_EN'], 'Obock'], '#64b5f6',
            ['==', ['get', 'ADM1_EN'], 'Tadjourah'], '#42a5f5',
            ['==', ['get', 'ADM1_EN'], 'Djibouti'], '#2196f3',
            '#81c784' // couleur par défaut
          ],
          'fill-opacity': 0.1
        }
      })

      // Contours des sous-préfectures (lignes fines)
      this.map.addLayer({
        id: 'admin2-boundaries',
        type: 'line',
        source: 'admin-boundaries',
        filter: ['has', 'ADM2_EN'],
        paint: {
          'line-color': '#81c784',
          'line-width': 0.8,
          'line-opacity': 0.6
        }
      })

      // Contours des régions (lignes moyennes)
      this.map.addLayer({
        id: 'admin1-boundaries',
        type: 'line',
        source: 'admin-boundaries',
        filter: ['has', 'ADM1_EN'],
        paint: {
          'line-color': '#1976d2',
          'line-width': 2,
          'line-opacity': 0.8
        }
      })

      // Labels des régions avec style amélioré
      this.map.addLayer({
        id: 'admin1-labels',
        type: 'symbol',
        source: 'admin-boundaries',
        filter: ['has', 'ADM1_EN'],
        layout: {
          'text-field': ['get', 'ADM1_EN'],
          'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
          'text-size': 14,
          'text-transform': 'uppercase',
          'text-letter-spacing': 0.1,
          'text-offset': [0, 0.5],
          'text-anchor': 'center'
        },
        paint: {
          'text-color': '#0d47a1',
          'text-halo-color': 'rgba(255,255,255,0.8)',
          'text-halo-width': 2
        }
      })

      // Labels des sous-préfectures (plus petits)
      this.map.addLayer({
        id: 'admin2-labels',
        type: 'symbol',
        source: 'admin-boundaries',
        filter: ['has', 'ADM2_EN'],
        layout: {
          'text-field': ['get', 'ADM2_EN'],
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-size': 11,
          'text-offset': [0, 0.3],
          'text-anchor': 'center'
        },
        paint: {
          'text-color': '#388e3c',
          'text-halo-color': 'rgba(255,255,255,0.7)',
          'text-halo-width': 1
        }
      })

      console.log('Administrative boundaries loaded successfully')

    } catch (error) {
      console.error('Erreur lors du chargement des frontières administratives:', error)
    }
  }
}